export enum VMwareInstanceTypes {
    i3 = 'i3.metal',
    i3en = 'i3en.metal',
    i4i = 'i4i.metal',
    recommend = 'recommend',
}

/**
 * Represents the unique configuration for that region.
 * 
 * VMware is considered enabled if supportedVMwareInstanceTypes is non-empty, otherwise VMWare is considered to be disabled in that region.
 */
export interface RegionConfig {
    label: string;
    adsEnabled?: boolean;
    supportedVmwareInstanceTypes: readonly VMwareInstanceTypes[]
};

export interface RegionsConfig {
    [region: string]: RegionConfig
}

export const isVmwareEnabled = (regionConfig: RegionConfig): boolean => {
    return !!(regionConfig.supportedVmwareInstanceTypes && regionConfig.supportedVmwareInstanceTypes.length);
};

const disabledVMware = [] as const;
const defaultVMwareInstanceTypes = [VMwareInstanceTypes.i3, VMwareInstanceTypes.i3en, VMwareInstanceTypes.recommend] as const;
const vmwareInstanceTypesWithI4i = [...defaultVMwareInstanceTypes, VMwareInstanceTypes.i4i] as const;

export const regions: RegionsConfig = {
    'us-east-2': {
        label: 'US East (Ohio)',
        supportedVmwareInstanceTypes: vmwareInstanceTypesWithI4i
    },
    'us-east-1': {
        adsEnabled: true,
        label: 'US East (N. Virginia)',
        supportedVmwareInstanceTypes: vmwareInstanceTypesWithI4i
    },
    'us-west-1': {
        label: 'US West (N. California)',
        supportedVmwareInstanceTypes: vmwareInstanceTypesWithI4i
    },
    'us-west-2': {
        adsEnabled: true,
        label: 'US West (Oregon)',
        supportedVmwareInstanceTypes: vmwareInstanceTypesWithI4i
    },
    'af-south-1': {
        label: 'Africa (Cape Town)',
        supportedVmwareInstanceTypes: defaultVMwareInstanceTypes
    },
    'ap-east-1': {
        label: 'Asia Pacific (Hong Kong)',
        supportedVmwareInstanceTypes: vmwareInstanceTypesWithI4i
    },
    'ap-south-1': {
        label: 'Asia Pacific (Mumbai)',
        supportedVmwareInstanceTypes: defaultVMwareInstanceTypes
    },
    'ap-south-2': {
        label: 'Asia Pacific (Hyderabad)',
        supportedVmwareInstanceTypes: disabledVMware
    },
    'ap-northeast-3': {
        label:'Asia Pacific (Osaka)',
        supportedVmwareInstanceTypes: defaultVMwareInstanceTypes
    },
    'ap-northeast-2': {
        label: 'Asia Pacific (Seoul)',
        supportedVmwareInstanceTypes: defaultVMwareInstanceTypes
    },
    'ap-southeast-1': {
        label: 'Asia Pacific (Singapore)',
        supportedVmwareInstanceTypes: vmwareInstanceTypesWithI4i
    },
    'ap-southeast-2': {
        adsEnabled: true,
        label: 'Asia Pacific (Sydney)',
        supportedVmwareInstanceTypes: vmwareInstanceTypesWithI4i
    },
    'ap-southeast-3': {
        adsEnabled: false,
        label: 'Asia Pacific (Jakarta)',
        supportedVmwareInstanceTypes: disabledVMware
    },
    'ap-southeast-4': {
        adsEnabled: false,
        label: 'Asia Pacific (Melbourne)',
        supportedVmwareInstanceTypes: disabledVMware
    },
    'ap-northeast-1': {
        adsEnabled: true,
        label: 'Asia Pacific (Tokyo)',
        supportedVmwareInstanceTypes: vmwareInstanceTypesWithI4i
    },
    'ca-central-1': {
        label: 'Canada (Central)',
        supportedVmwareInstanceTypes: vmwareInstanceTypesWithI4i
    },
    'cn-north-1': {
        label: 'China (Beijing)',
        supportedVmwareInstanceTypes: disabledVMware
    },
    'cn-northwest-1': {
        label: 'China (Ningxia)',
        supportedVmwareInstanceTypes: disabledVMware
    },
    'eu-central-1': {
        adsEnabled: true,
        label: 'Europe (Frankfurt)',
        supportedVmwareInstanceTypes: vmwareInstanceTypesWithI4i
    },
    'eu-central-2': {
        label: 'Europe (Zurich)',
        supportedVmwareInstanceTypes: disabledVMware
    },
    'eu-west-1': {
        adsEnabled: true,
        label: 'Europe (Ireland)',
        supportedVmwareInstanceTypes: vmwareInstanceTypesWithI4i
    },
    'eu-west-2': {
        adsEnabled: true,
        label: 'Europe (London)',
        supportedVmwareInstanceTypes: vmwareInstanceTypesWithI4i
    },
    'eu-south-1': {
        label: 'Europe (Milan)',
        supportedVmwareInstanceTypes: defaultVMwareInstanceTypes
    },
    'eu-south-2': {
        label: 'Europe (Spain)',
        supportedVmwareInstanceTypes: disabledVMware,
    },
    'eu-west-3': {
        label: 'Europe (Paris)',
        supportedVmwareInstanceTypes: vmwareInstanceTypesWithI4i
    },
    'eu-north-1': {
        label: 'Europe (Stockholm)',
        supportedVmwareInstanceTypes: defaultVMwareInstanceTypes
    },
    'me-south-1': {
        label: 'Middle East (Bahrain)',
        supportedVmwareInstanceTypes: disabledVMware,
    },
    'me-central-1': {
        label: 'Middle East (UAE)',
        supportedVmwareInstanceTypes: disabledVMware,
    },
    'sa-east-1': {
        label: 'South America (São Paulo)',
        supportedVmwareInstanceTypes: defaultVMwareInstanceTypes
    },
    'us-gov-east-1': {
        label: 'AWS GovCloud (US-East)',
        supportedVmwareInstanceTypes: disabledVMware,
    },
    'us-gov-west-1': {
        label: 'AWS GovCloud (US-West)',
        supportedVmwareInstanceTypes: disabledVMware,
    },
} as const;



export const regionList = Object.keys(regions).map(region => ({
    id: region,
    label: regions[region].label,
}));
export const ADSRegions: string[] = Object.keys(regions).filter(region => regions[region].adsEnabled);
